import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { ArrowUpRight, Truck, Factory, ClipboardList, Wrench, CheckCircle2, Timer } from "lucide-react";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import RelatedReading from "@/components/RelatedReading";

/**
 * Pillar / advisory page — "Fast Office Furniture Installation".
 *
 * Purpose: rank for time-sensitive long-tail queries like
 *  "office furniture installation timeline India",
 *  "how long does office furniture installation take",
 *  "fast office setup 100 seats".
 *
 * Strategy: pure E-E-A-T educational content + verifiable timelines
 *  (no fabricated guarantees). FAQs are written for AI-citation
 *  (Gemini / ChatGPT / Perplexity).
 */

const TIMELINE_PHASES = [
  {
    title: "Day 0 — Purchase order & specification freeze",
    icon: ClipboardList,
    body: "Once the purchase order is received and the CAD layout, BOM (bill of materials) and finishes are signed off, manufacturing is scheduled into the next available production slot at the Bhiwandi facility. This day-zero step locks panel sizes, laminate codes, hardware specification and seat counts — preventing change-orders mid-production.",
  },
  {
    title: "Day 1–10 — In-house manufacturing",
    icon: Factory,
    body: "Worktops, panels, pedestals, partitions and metal frames are cut, edge-banded, drilled and powder-coated in-house. Standard linear workstations and storage units complete in 7–10 working days; height-adjustable desks and acoustic pods extend to 10–14 days because of mechanical / acoustic sub-assemblies.",
  },
  {
    title: "Day 11–12 — Quality check & dispatch",
    icon: CheckCircle2,
    body: "Every batch is inspected against the signed BOM — dimensions, finish, hardware torque, drawer alignment, electrical components. Approved units are wrapped, palletised and loaded onto company-coordinated transport.",
  },
  {
    title: "Day 12–13 — Delivery to site",
    icon: Truck,
    body: "Pan-India delivery typically takes 1–3 days from the Bhiwandi facility — same-day to Mumbai/Thane/Navi Mumbai, 1–2 days to Pune/Nashik, 2–3 days to Bangalore/Hyderabad/Delhi NCR. Materials are unloaded directly onto the client floor with the installation crew.",
  },
  {
    title: "Day 13–16 — On-site installation",
    icon: Wrench,
    body: "A 100-seat office is typically installed in 3–5 working days by a 4–6 member crew: workstations first, then partitions, cabins, storage and meeting pods. The crew handles assembly, levelling, cable routing, fit-and-finish and final clean-up — no third-party contractor needed.",
  },
  {
    title: "Day 16 — Handover & sign-off",
    icon: Timer,
    body: "Final walkthrough with the client facility/admin team, snag-list capture and on-the-spot rectifications. Warranty documents, maintenance guidelines and the as-installed layout are handed over. The floor is occupancy-ready.",
  },
];

const TIMELINE_TABLE = [
  { scope: "10–25 seats (single team / startup)", manufacturing: "5–7 days", install: "1–2 days", total: "2 weeks" },
  { scope: "50 seats (mid-size team)", manufacturing: "7–10 days", install: "2–3 days", total: "2–3 weeks" },
  { scope: "100 seats (corporate floor)", manufacturing: "10–14 days", install: "3–5 days", total: "3–4 weeks" },
  { scope: "250 seats (BPO / IT floor)", manufacturing: "14–21 days (phased)", install: "5–8 days (phased)", total: "4–6 weeks" },
  { scope: "500+ seats (HQ roll-out)", manufacturing: "Phased — 3–4 batches", install: "Phased — by zone", total: "6–10 weeks" },
];

const FAQS = [
  {
    q: "How long does office furniture installation take?",
    a: "On-site installation of office furniture typically takes 1–5 working days depending on seat count: 1–2 days for 10–25 seats, 2–3 days for 50 seats, 3–5 days for a full 100-seat corporate floor. This is the installation phase only — total project timeline including manufacturing and delivery is usually 3–4 weeks for a 100-seat office.",
  },
  {
    q: "What is a typical office furniture project timeline in India?",
    a: "For a 100-seat corporate office in India, the typical end-to-end timeline is 3–4 weeks from purchase order: 10–14 days for in-house manufacturing, 1–3 days for delivery, 3–5 days for on-site installation, and 1 day for handover. Ezeefit manufactures at its Bhiwandi facility and handles the full execution under one roof, which compresses the timeline compared to multi-vendor projects.",
  },
  {
    q: "Can a 100-seat office be furnished in 2 weeks?",
    a: "A 100-seat office can be furnished in roughly 2.5–3 weeks if the CAD layout, BOM and finishes are signed off on day zero and the order is placed during a non-peak production slot. The constraints are manufacturing capacity (10–14 days for 100 workstations) and installation crew availability (3–5 days on-site). A pre-confirmed slot at the factory is the single biggest accelerator.",
  },
  {
    q: "What factors slow down office furniture installation?",
    a: "The most common delays are: (1) layout / specification changes after PO, (2) site-readiness issues — flooring, electrical, HVAC not complete, (3) restricted goods-lift or after-hours-only access at the building, (4) custom finishes requiring imported materials, (5) cabin/partition glass that needs longer toughening lead times. Ezeefit's design team flags all of these during the pre-PO site survey to keep the timeline on track.",
  },
  {
    q: "Does Ezeefit handle phased installation for occupied offices?",
    a: "Yes. For offices that remain occupied during fit-out, Ezeefit phases the installation by zone or after business hours (typically 7 pm to 7 am, including weekends). This is common for BPO / IT floors that cannot shut operations. A typical 250-seat phased installation runs 5–8 days of after-hours work with no daytime disruption.",
  },
  {
    q: "Is delivery included in office furniture installation?",
    a: "Yes. Every Ezeefit project includes manufacturing, delivery and on-site installation under a single project quote. No separate transport, labour or assembly contractor is required. Materials are dispatched from the Bhiwandi facility directly to the client site with the installation crew co-ordinated for the same window.",
  },
  {
    q: "What does the installation team do on site?",
    a: "Ezeefit's installation crew handles: workstation assembly and levelling, partition and cabin erection, cable and power routing within the desk spine, storage and pedestal placement, meeting-pod positioning and connection, glass-door alignment, chair unboxing and adjustment, and final clean-up. A site supervisor signs off snag-list rectifications before handover.",
  },
  {
    q: "Can installation begin before the entire site is ready?",
    a: "Partial installation can begin once the flooring is finished and the area is electrically and HVAC-ready in the first zone, even if other zones are still under civil work. This is a common time-saving technique for large floors — the installation crew works zone-by-zone behind the civil team, compressing the overall handover by 5–10 days.",
  },
];

const PageBlock = ({ icon: Icon, title, body }) => (
  <div className="flex gap-5 p-6 bg-white border border-[#E2E8F0] hover:border-[#F97316] transition-colors duration-300">
    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#0F172A] text-[#F97316]">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
    </div>
    <div className="flex-1">
      <h3 className="text-base font-semibold text-[#0F172A] mb-2">{title}</h3>
      <p className="text-sm text-[#475569] leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function FastOfficeInstallationPage() {
  const canonicalUrl = "https://ezeefitofficesystems.in/fast-office-installation";

  useCanonical(canonicalUrl);
  useMetaDescription("Office furniture installation timeline — manufacturing, delivery & on-site fit-out for 25-500+ seat corporate offices. Verifiable plan by Ezeefit.");
  useEffect(() => {
    document.title = "Fast Office Furniture Installation Timeline India | Ezeefit Office Systems";
  }, []);

  const breadcrumbJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://ezeefitofficesystems.in/" },
      { "@type": "ListItem", position: 2, name: "Fast Office Installation", item: canonicalUrl },
    ],
  });

  const articleJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Fast Office Furniture Installation — End-to-End Timeline & Process",
    description: "Technical reference for office furniture installation timelines in India — manufacturing, delivery and on-site installation phases for 25- to 500+ seat corporate offices, with phased-fit-out options for occupied workspaces.",
    author: { "@type": "Organization", name: "Ezeefit Office Systems" },
    publisher: { "@type": "Organization", name: "Ezeefit Office Systems", legalName: "Ezeefit Modular Furniture Private Limited" },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
  });

  const faqJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  });

  const howToJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How office furniture is manufactured, delivered and installed",
    description: "Six-phase end-to-end office furniture project workflow — from purchase order to handover.",
    totalTime: "P21D",
    step: TIMELINE_PHASES.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.body })),
  });

  return (
    <div className="pt-20" data-testid="fast-office-installation-page">
      <Helmet title="Fast Office Furniture Installation Timeline India | Ezeefit Office Systems">
        <meta name="description" content="Office furniture installation timeline — manufacturing, delivery & on-site fit-out for 25-500+ seat corporate offices. Verifiable plan by Ezeefit." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Fast Office Furniture Installation Timeline India | Ezeefit Office Systems" />
        <meta property="og:description" content="Phase-by-phase office furniture installation timeline — from PO to handover. Manufacturing, delivery and on-site fit-out for 25 to 500+ seat corporate floors in India." />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: howToJson }} />

      {/* HERO */}
      <section className="relative bg-[#0F172A] overflow-hidden noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 lg:py-32 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-3 px-4 py-2 border border-[#F97316]/40 mb-8" data-testid="install-eyebrow">
              <span className="w-2 h-2 bg-[#F97316] rounded-full" />
              <span className="text-xs font-mono tracking-widest uppercase text-[#F97316]">Office Fit-Out · Project Timeline</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-8 max-w-4xl" data-testid="install-h1">
              Fast Office<br />
              <span className="text-[#F97316]">Furniture Installation</span>
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-3xl">
              The end-to-end timeline for furnishing a corporate office in India — manufacturing, delivery and on-site installation. Verifiable phase-by-phase plan for 25 to 500+ seat floors, with phased-fit-out options for occupied workspaces.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ATOMIC ANSWER */}
      <section className="bg-white py-16 border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-[#F8FAFC] border-l-4 border-[#F97316] p-6 md:p-8" data-testid="install-atomic-answer">
            <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-3 block">In one paragraph</span>
            <p className="text-base md:text-lg text-[#0F172A] leading-relaxed">
              A typical 100-seat corporate office in India is fully furnished in <strong>3–4 weeks</strong> from purchase order — 10–14 days for in-house manufacturing, 1–3 days for delivery, 3–5 days for on-site installation, and 1 day for handover. Ezeefit Office Systems manufactures at its Bhiwandi (Thane) facility and handles design, manufacturing, delivery and installation under one roof.
            </p>
          </div>
        </div>
      </section>

      {/* TIMELINE TABLE */}
      <section className="bg-white py-20" data-testid="install-timeline-table-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] mb-3 tracking-tight">Office Installation Timelines by Seat Count</h2>
          <p className="text-sm text-[#475569] mb-10 max-w-3xl">Typical end-to-end project durations from purchase order to handover. Phased fit-out compresses timelines for larger floors by overlapping manufacturing batches with on-site installation.</p>
          <div className="overflow-x-auto border border-[#E2E8F0]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#0F172A] text-left">
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Project scope</th>
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Manufacturing</th>
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">On-site install</th>
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">End-to-end</th>
                </tr>
              </thead>
              <tbody>
                {TIMELINE_TABLE.map((row, i) => (
                  <tr key={i} className="border-b border-[#E2E8F0] last:border-b-0">
                    <td className="px-5 py-3 text-[#0F172A] font-medium">{row.scope}</td>
                    <td className="px-5 py-3 text-[#475569] font-mono">{row.manufacturing}</td>
                    <td className="px-5 py-3 text-[#475569] font-mono">{row.install}</td>
                    <td className="px-5 py-3 text-[#64748B] font-mono">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PROJECT PHASES */}
      <section className="bg-[#F8FAFC] py-20" data-testid="install-phases-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-10">
            <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-2 block">End-to-End Workflow</span>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight">From purchase order to handover</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {TIMELINE_PHASES.map((step, i) => (
              <PageBlock key={i} icon={step.icon} title={step.title} body={step.body} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 border-t border-[#E2E8F0]" data-testid="install-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-3 block">Frequently Asked Questions</span>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-10">Installation Timeline Q&amp;A</h2>
          <div className="space-y-4">
            {FAQS.map((item, i) => (
              <details key={i} className="group bg-[#F8FAFC] border border-[#E2E8F0] open:border-[#F97316] transition-colors" {...(i === 0 ? { open: true } : {})} data-testid={`install-faq-${i}`}>
                <summary className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-4">
                  <h3 className="text-base font-semibold text-[#0F172A] leading-snug">{item.q}</h3>
                  <span className="flex-shrink-0 w-6 h-6 border border-[#E2E8F0] flex items-center justify-center text-[#475569] group-open:bg-[#F97316] group-open:text-white group-open:border-[#F97316] transition-colors">
                    <span className="block group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                  </span>
                </summary>
                <div className="px-6 pb-5 -mt-1 text-sm text-[#475569] leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related Reading — bidirectional blog interlinking (SEO topic clusters) */}
      <RelatedReading
        keywords={["installation", "timeline", "delivery", "project", "workstations", "lead time"]}
        heading="Related Insights on Office Installation"
        subheading="Practical articles on project timelines and delivery planning."
        testId="related-reading-pillar-install"
      />

      {/* CTA */}
      <section className="bg-[#0F172A] py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-3">Need an office furnished on a tight timeline?</h2>
            <p className="text-[#94A3B8] text-base leading-relaxed">Share your seat count, floor area and target occupancy date — Ezeefit's project team will return a phase-by-phase timeline within one business day, free of cost.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold tracking-wide uppercase transition-colors" data-testid="install-cta-contact">
            Get a Project Timeline
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
          </Link>
        </div>
      </section>
    </div>
  );
}
